// web/src/pages/NfcTap.tsx
//
// /nfc?uid=<x>&ctr=<y>&cmac=<z> — the page an NFC tag opens. Reads the tap
// params and either claims the stamp immediately (signed in) or stashes the
// tap and runs the app's normal sign-in, landing the user back here to claim
// once authenticated (the onboarding funnel).
//
// Login note: OAuth always comes back to the fixed /auth/callback URI, so the
// intended destination travels via stashPostLoginDest()/takePostLoginDest()
// (lib/post-login-dest.ts) — HMAC-verified, TTL-bounded, with a Safari-ITP
// fallback. '/nfc' is on that module's allowlist and the query string survives
// resolution, so the tap params come back with us.
//
// ── Why this page redirects to the pass on success ──────────────────────────
//
// It is not decoration. `nfc-tap` (api/stamp.ts) writes an APPROVED
// stamp_requests row carrying a server attestation, with onchain_status:'none'
// — the server never signs the chain transaction. Only the customer's own
// device can submit `record_purchase_attested`, and the thing that does it is
// the Dashboard: on mount it calls getStuckStamps() and drives any
// approved/unsubmitted row to chain.
//
// So a customer who tapped, read "Stamped!", and closed the tab left a stamp
// stranded off-chain until they next happened to open the app. Sending them
// into the app IS what finishes the stamp. The pass is the right landing spot
// for the same reason Nik asked for it: PixelPass animates the newly-filled
// slot, so the payoff is visible rather than implied.

import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { getSessionId } from '../services/api-client';
import { claimNfcStamp, ApiError, type NfcClaimResult } from '../services/nfc-service';
import { stashPostLoginDest } from '../lib/post-login-dest';

type State =
  | { k: 'loading' }
  | { k: 'needsLogin' }
  | { k: 'done'; result: NfcClaimResult }
  | { k: 'error'; message: string };

/** Where a successful tap lands. `tab=pass` is useTabParam's own scheme. */
export const NFC_SUCCESS_DEST = '/?tab=pass';

/**
 * Long enough to read "Stamped!", short enough not to feel like a wait. The
 * confirmation is the only feedback the customer gets for the tap, so it must
 * actually register — an instant redirect would flash it past them.
 */
export const NFC_REDIRECT_MS = 1600;

export default function NfcTap() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  // The ROUTER's search string, not window.location.search: the router is
  // what actually resolved this route, and reading the global bypasses it.
  const { search } = useLocation();
  const [state, setState] = useState<State>({ k: 'loading' });
  // The claim must fire exactly once. `params` is a fresh object on every
  // render, so an unguarded effect keyed on it would re-POST the tap — and the
  // second POST spends a counter that the first already consumed, turning a
  // good tap into "already got this stamp".
  const claimed = useRef(false);

  useEffect(() => {
    const uid = params.get('uid');
    const ctr = params.get('ctr');
    const cmac = params.get('cmac');
    if (!uid || !ctr || !cmac) { setState({ k: 'error', message: 'This tap link is incomplete.' }); return; }

    // Not signed in → offer the funnel. The destination is stashed at click
    // time rather than here, so merely viewing this page never plants a
    // redirect that a later unrelated login could pick up.
    if (!getSessionId()) {
      setState({ k: 'needsLogin' });
      return;
    }
    if (claimed.current) return;
    claimed.current = true;
    (async () => {
      try {
        const result = await claimNfcStamp({ uid, ctr, cmac });
        setState({ k: 'done', result });
      } catch (e) {
        const msg = e instanceof ApiError
          ? (e.status === 409 ? "You've already got this stamp." :
             e.status === 429 ? 'One moment — try again in a few seconds.' :
             e.status === 401 ? "This tag couldn't be verified." :
             e.status === 404 ? "We don't recognize this tag." : e.message)
          : 'Something went wrong. Try tapping again.';
        setState({ k: 'error', message: msg });
      }
    })();
  }, [params]);

  // Hand off to the app once the stamp is banked. `replace` so Back exits to
  // wherever they came from rather than re-opening a tap URL whose counter has
  // already been spent — revisiting it would read as "already got this stamp".
  useEffect(() => {
    if (state.k !== 'done') return;
    const t = setTimeout(() => navigate(NFC_SUCCESS_DEST, { replace: true }), NFC_REDIRECT_MS);
    return () => clearTimeout(t);
  }, [state.k, navigate]);

  if (state.k === 'loading') return <Centered><Line>Reading your tap…</Line></Centered>;

  if (state.k === 'needsLogin') {
    return (
      <Centered>
        <Title>☕ Get your first stamp</Title>
        <Line>Sign in and your stamp is waiting.</Line>
        <button
          type="button"
          onClick={() => {
            // Stash /nfc?uid&ctr&cmac so the login screen's resolveFinalDest()
            // brings us back here with the tap intact once the exchange
            // completes, then hand off to the REAL login screen.
            //
            // This used to call startGoogleLogin() directly, which quietly made
            // the tag a Google-only door: Apple sign-in has been live since
            // 2026-08-03 and an Apple customer tapping a tag in a café had no
            // way in. '/' renders OAuthLogin when signed out, and that screen
            // offers both providers — one door, not two.
            stashPostLoginDest('/nfc' + search, Date.now());
            navigate('/', { replace: true });
          }}
          className="pixel-corners bg-coral text-white font-grotesk font-bold text-[15px] py-3.5 px-6 mt-5"
        >
          Sign in
        </button>
      </Centered>
    );
  }

  if (state.k === 'done') {
    const { quantity, shop, birthday } = state.result;
    return (
      <Centered>
        <Title>Stamped! ✓</Title>
        <Line>+{quantity} at {shop}</Line>
        {birthday && <Line>🎂 {birthday.title}</Line>}
        {/* A manual way through, so a blocked/backgrounded timer never strands
            anyone on a dead end — and so the stamp still reaches the chain. */}
        <button
          type="button"
          onClick={() => navigate(NFC_SUCCESS_DEST, { replace: true })}
          className="pixel-corners bg-coral text-white font-grotesk font-bold text-[15px] py-3.5 px-6 mt-5"
        >
          See my pass →
        </button>
      </Centered>
    );
  }

  return (
    <Centered>
      <Title>Hmm.</Title>
      <Line>{state.message}</Line>
      {/* Errors do NOT auto-redirect: bouncing away from a failure hides it,
          and the customer needs to be able to show this to the barista. */}
      <button
        type="button"
        onClick={() => navigate(NFC_SUCCESS_DEST, { replace: true })}
        className="pixel-corners bg-white border-2 border-hairline text-body-grey font-grotesk font-bold text-[14px] py-3 px-5 mt-5"
      >
        Open my pass
      </button>
    </Centered>
  );
}

function Title({ children }: { children: React.ReactNode }) {
  return <h1 className="font-body font-bold text-3xl leading-[1.1] text-primary mb-2">{children}</h1>;
}

function Line({ children }: { children: React.ReactNode }) {
  return <p className="font-grotesk font-medium text-[15px] text-body-grey">{children}</p>;
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-[60vh] grid place-items-center text-center px-6 py-8">
      <div className="flex flex-col items-center">{children}</div>
    </div>
  );
}
